import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import NavLogo from '../../assets/pixeloop-logo.png'
import PixeChainLogo from '../../assets/pixechain_tp.png'
import { useMe } from '../../hooks/useMe'
import { clearToken } from '../../lib/auth'
import { useInboxWS } from '../../hooks/useInboxWS'
import { getNotificationCount } from '../../api/messenger'
import { getInboxCount } from '../../api/prosRequests'
import { useSessionFlags, markOnboardingComplete } from '../../state/session'
import MobileModeDropdownPill from './MobileModeDropdownPill'
import NavUserAvatar from './NavUserAvatar'

type NavBarProps = {
  showSearch?: boolean
  /** Only for pages that need full-viewport content behind the navbar (e.g. mobile fullscreen feed). */
  mobileFixed?: boolean
  /** Show/hide mobile hamburger menu button in the top-right cluster (below md). */
  showMobileMenuButton?: boolean
  /** Mobile-only app mode pill dropdown (Publish/People/Store/Saved). */
  showMobileModePill?: boolean
  /** Fine-tune avatar/menu horizontal offset in px (positive = move right) */
  avatarOffsetX?: number
  /** Fine-tune avatar/menu vertical offset in px (positive = move down) */
  avatarOffsetY?: number
}

export default function NavBar({
  showSearch = true,
  mobileFixed = false,
  showMobileMenuButton = true,
  showMobileModePill = true,
  avatarOffsetX = 0,
  avatarOffsetY = 0,
}: NavBarProps) {
  const { isAuthenticated, hasCompletedOnboarding } = useSessionFlags()
  const authed = isAuthenticated
  const { me } = useMe(authed)
  const { lastUpdate } = useInboxWS()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [appsOpen, setAppsOpen] = useState(false)
  const [activeApp, setActiveApp] = useState<'publish' | 'people' | 'pages' | 'store' | 'saved'>('publish')
  const appsRef = useRef<HTMLDivElement | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [unread, setUnread] = useState<number>(0)
  const [prosRequestsPending, setProsRequestsPending] = useState<number>(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement | null>(null)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const mobileSearchRef = useRef<HTMLInputElement | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)
  const navAvatarUrl = String(me?.profile?.avatar_url || me?.avatar_url || '').trim()
  const navAvatarLabel = (
    me?.profile?.display_name ||
    me?.display_name ||
    me?.profile?.handle ||
    me?.username ||
    '?'
  ).toString().trim()

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node
      if (menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false)
      if (appsRef.current && !appsRef.current.contains(target)) setAppsOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  // Expose navbar height for other mobile chrome (e.g. TikTok feed overlays) without hardcoding.
  useEffect(() => {
    const el = headerRef.current
    if (!el || typeof window === 'undefined') return

    let raf = 0
    const setVar = () => {
      window.cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(() => {
        const h = Math.ceil(el.getBoundingClientRect().height)
        document.documentElement.style.setProperty('--nav-h', `${h}px`)
      })
    }

    setVar()
    const ro = new ResizeObserver(setVar)
    ro.observe(el)
    window.addEventListener('resize', setVar)
    return () => {
      window.cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', setVar)
    }
  }, [])

  // Close the search overlay when crossing into desktop (lg+) so it can't "stick" after resize.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1024px)') // Tailwind lg
    const onChange = () => {
      if (mq.matches) setMobileSearchOpen(false)
    }
    onChange()
    // Safari < 14 uses addListener/removeListener
    try {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    } catch {
      // eslint-disable-next-line deprecation/deprecation
      mq.addListener(onChange)
      // eslint-disable-next-line deprecation/deprecation
      return () => mq.removeListener(onChange)
    }
  }, [])

  // If search is disabled for this page, force-close any overlay.
  useEffect(() => {
    if (!showSearch) setMobileSearchOpen(false)
  }, [showSearch])

  // Mobile drawer: focus management, ESC close, and basic focus trap.
  useEffect(() => {
    if (!mobileMenuOpen) return

    const prev = document.activeElement as HTMLElement | null
    const root = mobileMenuRef.current

    const focusFirst = () => {
      if (!root) return
      const els = Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      ;(els[0] ?? root).focus?.()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setMobileMenuOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      if (!root) return
      const els = Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('data-focus-guard'))
      if (els.length === 0) return
      const first = els[0]
      const last = els[els.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (!active || active === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    // allow animation frame so the panel is in DOM
    const t = window.setTimeout(focusFirst, 0)

    return () => {
      window.clearTimeout(t)
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKeyDown)
      prev?.focus?.()
    }
  }, [mobileMenuOpen])

  // Mobile search overlay: focus input and ESC close.
  useEffect(() => {
    if (!mobileSearchOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setMobileSearchOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const t = window.setTimeout(() => mobileSearchRef.current?.focus(), 0)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [mobileSearchOpen])

  // Sync search input with current page query (?q=...)
  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    const q = sp.get('q') || ''
    // only sync for people/store/publish/home/inbox contexts to avoid fighting page-specific inputs
    if (
      location.pathname.startsWith('/people') ||
      location.pathname.startsWith('/store') ||
      location.pathname.startsWith('/pages') ||
      location.pathname.startsWith('/inbox') ||
      location.pathname.startsWith('/publish') ||
      location.pathname === '/'
    ) {
      setSearch(q)
    } else {
      // leave as-is for other pages
    }
  }, [location.pathname, location.search])

  // Persist selected app mode (Publish/People/Store) in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('pixeloop_app_mode')
    if (saved === 'people' || saved === 'pages' || saved === 'store' || saved === 'publish' || saved === 'saved') {
      setActiveApp(saved)
    } else {
      // derive from current route
      if (location.pathname.startsWith('/people')) setActiveApp('people')
      else if (location.pathname.startsWith('/pages')) setActiveApp('pages')
      else if (location.pathname.startsWith('/store')) setActiveApp('store')
      else if ((location.pathname.startsWith('/publish') || location.pathname === '/') && new URLSearchParams(location.search).get('mode') === 'saved') setActiveApp('saved')
      else setActiveApp('publish')
    }
  }, []) // run once on mount

  useEffect(() => {
    if (location.pathname.startsWith('/people')) setActiveApp('people')
    else if (location.pathname.startsWith('/pages')) setActiveApp('pages')
    else if (location.pathname.startsWith('/store')) setActiveApp('store')
    else if ((location.pathname.startsWith('/publish') || location.pathname === '/') && new URLSearchParams(location.search).get('mode') === 'saved') setActiveApp('saved')
    else if (location.pathname.startsWith('/publish') || location.pathname === '/') setActiveApp('publish')
  }, [location.pathname, location.search])

  // Live search by route mode: debounce and write ?q=... as source-of-truth.
  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    const onPeople = location.pathname.startsWith('/people')
    const onStore = location.pathname.startsWith('/store')
    const onPages = location.pathname.startsWith('/pages')
    const rootMode = (sp.get('mode') || 'all').toLowerCase()
    const onRootPublish = location.pathname === '/' && (rootMode === 'all' || rootMode === 'saved')
    const onPublishRoute = location.pathname.startsWith('/publish')
    if (!(onPeople || onStore || onPages || onRootPublish || onPublishRoute)) return
    const timer = window.setTimeout(() => {
      const nextQ = search.trim()
      const currentParams = new URLSearchParams(location.search)
      const currentQ = (currentParams.get('q') || '').trim()
      if (nextQ === currentQ) return
      if (nextQ) currentParams.set('q', nextQ)
      else currentParams.delete('q')
      const nextUrl = `${location.pathname}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`
      navigate(nextUrl, { replace: true })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search, location.pathname, location.search, navigate])

  const switchApp = (app: 'publish' | 'people' | 'pages' | 'store' | 'saved') => {
    setActiveApp(app)
    localStorage.setItem('pixeloop_app_mode', app)
    setAppsOpen(false)
    if (app === 'publish') navigate('/')
    if (app === 'saved') navigate('/?mode=saved')
    if (app === 'people') navigate('/people')
    if (app === 'pages') navigate('/pages')
    if (app === 'store') navigate('/store')
  }

  useEffect(() => {
    if (!isAuthenticated) return
    if (hasCompletedOnboarding) return
    if (!me) return
    if (me.profile?.handle) {
      markOnboardingComplete(true)
      return
    }
    if (location.pathname.startsWith('/onboarding')) return
    navigate('/onboarding/handlepicker', { replace: true })
  }, [isAuthenticated, hasCompletedOnboarding, me, location.pathname, navigate])

  // Inbox badge: seed and refresh on WS updates
  useEffect(() => {
    if (!authed) { setUnread(0); return }
    let cancelled = false
    const fetchCount = async () => {
      try {
        const { unread } = await getNotificationCount()
        if (!cancelled) setUnread(unread)
      } catch {}
    }
    fetchCount()
    // refresh shortly after WS activity
    if (lastUpdate) {
      const t = setTimeout(fetchCount, 150)
      return () => { cancelled = true; clearTimeout(t) }
    }
    return () => { cancelled = true }
  }, [authed, lastUpdate])

  useEffect(() => {
    if (!authed) {
      setProsRequestsPending(0)
      return
    }
    let cancelled = false
    getInboxCount()
      .then((res) => {
        if (!cancelled) setProsRequestsPending(Number(res?.pending_count || 0))
      })
      .catch(() => {
        if (!cancelled) setProsRequestsPending(0)
      })
    return () => {
      cancelled = true
    }
  }, [authed, lastUpdate])

  function submitSearch() {
    const q = search.trim()
    if (location.pathname.startsWith('/people')) {
      navigate(q ? `/people?q=${encodeURIComponent(q)}` : '/people')
    } else if (location.pathname.startsWith('/inbox')) {
      navigate(q ? `/inbox?q=${encodeURIComponent(q)}` : '/inbox')
    } else if (location.pathname.startsWith('/store')) {
      navigate(q ? `/store?q=${encodeURIComponent(q)}` : '/store')
    } else if (location.pathname.startsWith('/pages')) {
      navigate(q ? `/pages?q=${encodeURIComponent(q)}` : '/pages')
    } else if (location.pathname.startsWith('/publish')) {
      const sp = new URLSearchParams(location.search)
      const mode = sp.get('mode')
      if (mode === 'saved') {
        navigate(q ? `/publish?mode=saved&q=${encodeURIComponent(q)}` : '/publish?mode=saved')
      } else {
        navigate(q ? `/publish?q=${encodeURIComponent(q)}` : '/publish')
      }
    } else if (location.pathname === '/') {
      const sp = new URLSearchParams(location.search)
      const mode = sp.get('mode')
      if (mode === 'saved') {
        navigate(q ? `/?mode=saved&q=${encodeURIComponent(q)}` : '/?mode=saved')
      } else {
        navigate(q ? `/?q=${encodeURIComponent(q)}` : '/')
      }
    } else {
      navigate(q ? `/?q=${encodeURIComponent(q)}` : '/')
    }
  }

  function updateSearchValue(next: string) {
    setSearch(next)
    if (location.pathname.startsWith('/people')) {
      try {
        window.dispatchEvent(new CustomEvent('pixeloop:peopleSearchInput', { detail: { q: next } }))
      } catch {}
    }
  }

  function submitSearchMobile() {
    submitSearch()
    setMobileSearchOpen(false)
    setMobileMenuOpen(false)
  }

  const profileHref = me?.profile?.public_id ? `/people/i/${encodeURIComponent(me.profile.public_id)}` : null

  return (
    <header
      ref={headerRef as any}
      data-no-tapback="1"
      className={[
        mobileFixed ? 'fixed md:sticky left-0 right-0 md:left-auto md:right-auto' : 'sticky',
        'top-0 z-40 w-full',
        // In mobile TikTok mode we want the media to show behind the navbar.
        mobileFixed ? 'border-transparent bg-transparent backdrop-blur-0 md:border-white/10 md:bg-neutral-950/70 md:backdrop-blur' : 'border-b border-white/10 bg-neutral-950/70 backdrop-blur',
      ].join(' ')}
    >
      {!authed && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup') && (
        <div className="bg-amber-500/15 border-b border-amber-400/30 text-amber-50 text-sm px-4 py-2 flex items-center justify-between">
          <div>You’re signed out. Log in to keep editing and stay connected.</div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="px-3 py-1 rounded-lg border border-amber-300/50 bg-amber-500/20 text-amber-50 hover:bg-amber-500/30 text-sm">
              Log in
            </Link>
          </div>
        </div>
      )}
      {/* Mobile header (below md) */}
      <div className="mx-auto max-w-7xl px-4 py-3 md:hidden relative">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={NavLogo} alt="PixeLoop" className="h-8 w-auto shrink-0" />
          </Link>
          <div className="flex items-center gap-1">
            {showSearch && (
              <button
                type="button"
                onClick={() => setMobileSearchOpen(true)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5"
                aria-label="Search"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                  <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.98 4 11.49 4 9 4 7 6.01 7 8.5S9 13 11.49 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4 3.99L20.49 18l-3.99-4z" />
                </svg>
              </button>
            )}
            <Link to="/inbox" className="relative inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5" aria-label="Inbox">
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              {authed && unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 border border-neutral-900">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Link>
            <Link to="/store" className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5" aria-label="Cart">
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                <path d="M7 18a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4zM7.16 14h9.48a2 2 0 001.94-1.52l1.24-4.96A1 1 0 0019 6H6.21l-.44-2.22A1 1 0 004.79 3H3a1 1 0 100 2h1.21l2.07 10.36A2 2 0 008.24 17h9.76a1 1 0 100-2H8.24l-.24-1z" />
              </svg>
            </Link>
            {authed && (
              <Link
                to="/settings"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/10 hover:bg-white/15 focus:outline-none overflow-hidden"
                aria-label="Settings"
                title={me?.profile?.handle || me?.username || 'Settings'}
              >
                <NavUserAvatar src={navAvatarUrl} label={navAvatarLabel} className="h-10 w-10" />
              </Link>
            )}
            {showMobileMenuButton && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5"
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded={mobileMenuOpen}
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white/85" aria-hidden="true">
                  <path d="M3 6h18M3 12h18M3 18h18" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {showMobileModePill && (
          <div className="absolute left-1/2 top-full -translate-x-1/2 mt-2">
            {/* App-style mode pill: float on its own line below the header row without increasing navbar height. */}
            <MobileModeDropdownPill active={activeApp} onSelect={(k) => switchApp(k)} />
          </div>
        )}
      </div>

      {/* Tablet header (md..lg): 2-row layout to prevent search from overlapping the top row. */}
      <div className="hidden md:block lg:hidden mx-auto max-w-7xl px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="mr-2 h-9 w-9 rounded-full hover:bg-white/5"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={mobileMenuOpen}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white/80"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
            </button>
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src={NavLogo} alt="PixeLoop" className="h-[var(--logo-size)] w-auto shrink-0" />
            </Link>
          </div>
          <nav className="flex items-center justify-end gap-2 text-sm whitespace-nowrap">
            {/* Apps dropdown (Publish / People / Store) */}
            <div ref={appsRef} className="relative">
              <button
                onClick={() => setAppsOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-white/5"
                aria-haspopup="menu"
                aria-expanded={appsOpen}
              >
                <span>
                  {activeApp === 'publish'
                    ? 'Pixe Publish'
                    : activeApp === 'people'
                    ? 'Pixe People'
                    : activeApp === 'pages'
                    ? 'Pixe Pages'
                    : activeApp === 'store'
                    ? 'Pixe Store'
                    : 'Saved Pixes'}
                </span>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/70" aria-hidden="true">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              {appsOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur shadow-lg p-1 z-50">
                  <button onClick={() => switchApp('publish')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pixe Publish</span>
                  </button>
                  <button onClick={() => switchApp('people')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pixe People</span>
                  </button>
                  <button onClick={() => switchApp('pages')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pixe Pages</span>
                  </button>
                  <button onClick={() => switchApp('store')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pixe Store</span>
                  </button>
                  <button onClick={() => switchApp('saved')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Saved Pixes</span>
                  </button>
                </div>
              )}
            </div>

            {showSearch && (
              <button
                type="button"
                onClick={() => setMobileSearchOpen(true)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5"
                aria-label="Search"
                aria-haspopup="dialog"
                aria-expanded={mobileSearchOpen}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                  <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.98 4 11.49 4 9 4 7 6.01 7 8.5S9 13 11.49 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4 3.99L20.49 18l-3.99-4z" />
                </svg>
              </button>
            )}

            {/* Icon buttons: Inbox and Cart */}
            <Link to="/inbox" className="relative px-2 py-2 rounded-lg hover:bg-white/5" aria-label="Inbox">
              {/* Mail/envelope icon */}
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              {authed && unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 border border-neutral-900">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Link>
            <Link to="/store" className="px-2 py-2 rounded-lg hover:bg-white/5" aria-label="Cart">
              {/* Shopping cart icon */}
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                <path d="M7 18a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4zM7.16 14h9.48a2 2 0 001.94-1.52l1.24-4.96A1 1 0 0019 6H6.21l-.44-2.22A1 1 0 004.79 3H3a1 1 0 100 2h1.21l2.07 10.36A2 2 0 008.24 17h9.76a1 1 0 100-2H8.24l-.24-1z"/>
              </svg>
            </Link>
            {!authed ? (
              <>
                <Link to="/login" className="px-3 py-2 rounded-lg hover:bg-white/5 whitespace-nowrap">Log in</Link>
                <Link to="/signup" className="px-3 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 whitespace-nowrap">Sign up</Link>
              </>
              ) : (
                <div
                ref={menuRef}
                className="relative ml-4 pl-4 border-l border-white/10"
                style={{ transform: `translate(${avatarOffsetX}px, ${avatarOffsetY}px)` }}
              >
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/10 hover:bg-white/15 focus:outline-none"
                title={me?.profile?.handle || me?.username}
              >
                  <NavUserAvatar src={navAvatarUrl} label={navAvatarLabel} className="h-10 w-10" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur shadow-lg p-1">
                    {/* Settings first */}
                    <Link to="/settings" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                      <span>Settings</span>
                    </Link>
                    {/* Wallet */}
                  <Link to="/settings/wallet" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Wallet</span>
                  </Link>
                  <Link to="/pros/requests" className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pros Requests</span>
                    {prosRequestsPending > 0 ? <span className="text-xs text-white/65">{prosRequestsPending}</span> : null}
                  </Link>
                  <Link to="/jobs" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Jobs</span>
                  </Link>
                  {/* Pricing in dropdown */}
                  <Link to="/pricing" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pricing</span>
                  </Link>
                    {/* Sign out */}
                    <button onClick={()=>{ clearToken(); window.location.reload(); }} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                      <span>Sign out</span>
                    </button>
                  </div>
                )}
                </div>
              )}
              <img src={PixeChainLogo} alt="PixeChain" className="h-7 w-auto opacity-90 ml-2" />
          </nav>
        </div>
      </div>

      {/* Desktop header (lg+): original 3/6/3 grid layout. */}
      <div className="hidden lg:grid mx-auto max-w-7xl px-4 py-3 grid-cols-12 items-center gap-3">
        <div className="col-span-3 flex items-center gap-2 min-w-0">
          <button className="mr-2 h-9 w-9 rounded-full hover:bg-white/5" aria-label="Menu">
            <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white/80"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={NavLogo} alt="PixeLoop" className="h-[var(--logo-size)] w-auto shrink-0" />
          </Link>
        </div>

        <div className="col-span-6 flex items-center gap-4 min-w-0">
          {showSearch && (
            <div className="flex flex-1 min-w-0">
              <input
                placeholder="Search"
                value={search}
                onChange={(e) => updateSearchValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitSearch() }}
                aria-label="Search"
                className="min-w-0 flex-1 rounded-l-full bg-white/5 border border-white/10 px-4 py-2 outline-none focus:ring-2 focus:ring-brand"
              />
              <button onClick={submitSearch} className="shrink-0 rounded-r-full border border-l-0 border-white/10 px-4 bg-white/5" aria-label="Search">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/80"><path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.98 4 11.49 4 9 4 7 6.01 7 8.5S9 13 11.49 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4 3.99L20.49 18l-3.99-4z"/></svg>
              </button>
            </div>
          )}
        </div>

        <nav className="col-span-3 ml-auto flex items-center justify-end gap-2 text-sm whitespace-nowrap">
          {/* Apps dropdown (Publish / People / Store) */}
          <div ref={appsRef} className="relative">
            <button
              onClick={() => setAppsOpen((v) => !v)}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-white/5"
              aria-haspopup="menu"
              aria-expanded={appsOpen}
            >
              <span>
                {activeApp === 'publish'
                  ? 'Pixe Publish'
                  : activeApp === 'people'
                  ? 'Pixe People'
                  : activeApp === 'pages'
                  ? 'Pixe Pages'
                  : activeApp === 'store'
                  ? 'Pixe Store'
                  : 'Saved Pixes'}
              </span>
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/70" aria-hidden="true">
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            {appsOpen && (
              <div className="absolute right-0 mt-2 w-44 rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur shadow-lg p-1 z-50">
                <button onClick={() => switchApp('publish')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                  <span>Pixe Publish</span>
                </button>
                <button onClick={() => switchApp('people')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                  <span>Pixe People</span>
                </button>
                <button onClick={() => switchApp('pages')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                  <span>Pixe Pages</span>
                </button>
                <button onClick={() => switchApp('store')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                  <span>Pixe Store</span>
                </button>
                <button onClick={() => switchApp('saved')} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                  <span>Saved Pixes</span>
                </button>
              </div>
            )}
          </div>

          {/* Icon buttons: Inbox and Cart */}
          <Link to="/inbox" className="relative px-2 py-2 rounded-lg hover:bg-white/5" aria-label="Inbox">
            {/* Mail/envelope icon */}
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            {authed && unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 border border-neutral-900">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Link>
          <Link to="/store" className="px-2 py-2 rounded-lg hover:bg-white/5" aria-label="Cart">
            {/* Shopping cart icon */}
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
              <path d="M7 18a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4zM7.16 14h9.48a2 2 0 001.94-1.52l1.24-4.96A1 1 0 0019 6H6.21l-.44-2.22A1 1 0 004.79 3H3a1 1 0 100 2h1.21l2.07 10.36A2 2 0 008.24 17h9.76a1 1 0 100-2H8.24l-.24-1z"/>
            </svg>
          </Link>
          {!authed ? (
            <>
              <Link to="/login" className="px-3 py-2 rounded-lg hover:bg-white/5 whitespace-nowrap">Log in</Link>
              <Link to="/signup" className="px-3 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 whitespace-nowrap">Sign up</Link>
            </>
            ) : (
              <div
              ref={menuRef}
              className="relative ml-4 pl-4 border-l border-white/10"
              style={{ transform: `translate(${avatarOffsetX}px, ${avatarOffsetY}px)` }}
            >
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/10 hover:bg-white/15 focus:outline-none"
                title={me?.profile?.handle || me?.username}
              >
                <NavUserAvatar src={navAvatarUrl} label={navAvatarLabel} className="h-10 w-10" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur shadow-lg p-1">
                  {/* Settings first */}
                  <Link to="/settings" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Settings</span>
                  </Link>
                  {/* Wallet */}
                  <Link to="/settings/wallet" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Wallet</span>
                  </Link>
                  <Link to="/pros/requests" className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pros Requests</span>
                    {prosRequestsPending > 0 ? <span className="text-xs text-white/65">{prosRequestsPending}</span> : null}
                  </Link>
                  <Link to="/jobs" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Jobs</span>
                  </Link>
                  {/* Pricing in dropdown */}
                  <Link to="/pricing" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Pricing</span>
                  </Link>
                  {/* Sign out */}
                  <button onClick={()=>{ clearToken(); window.location.reload(); }} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5">
                    <span>Sign out</span>
                  </button>
                </div>
              )}
              </div>
            )}
            <img src={PixeChainLogo} alt="PixeChain" className="h-7 w-auto opacity-90 ml-2" />
          </nav>
      </div>

      {/* Mobile search overlay */}
      {showSearch && mobileSearchOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Search">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileSearchOpen(false)} />
          <div className="absolute left-0 right-0 top-0 p-4">
            <div className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-neutral-950/95 backdrop-blur p-3 shadow-2xl">
              <div className="flex items-center gap-2">
                <input
                  ref={mobileSearchRef}
                  placeholder="Search"
                  value={search}
                  onChange={(e) => updateSearchValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitSearchMobile() }}
                  aria-label="Search"
                  className="min-w-0 flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-brand"
                />
                <button
                  type="button"
                  onClick={submitSearchMobile}
                  className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                  aria-label="Search"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                    <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.98 4 11.49 4 9 4 7 6.01 7 8.5S9 13 11.49 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4 3.99L20.49 18l-3.99-4z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setMobileSearchOpen(false)}
                  className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                  aria-label="Close search"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                    <path d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.29 9.17 12 2.88 5.71 4.29 4.29 10.59 10.6l6.3-6.31z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right drawer for mobile/tablet (below lg) */}
      <div className={`fixed inset-0 z-50 lg:hidden ${mobileMenuOpen ? '' : 'pointer-events-none'}`} aria-hidden={!mobileMenuOpen}>
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          ref={mobileMenuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute right-0 top-0 h-full w-[340px] max-w-[90vw] bg-neutral-900 border-l border-white/10 shadow-2xl transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <img src={NavLogo} alt="PixeLoop" className="h-7 w-auto shrink-0" />
                <div className="text-sm font-semibold truncate">Menu</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5"
                aria-label="Close menu"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white/85" aria-hidden="true">
                  <path d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.29 9.17 12 2.88 5.71 4.29 4.29 10.59 10.6l6.3-6.31z" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {showSearch && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-white/50">Search</div>
                  <div className="flex items-center gap-2">
                    <input
                      placeholder="Search"
                      value={search}
                      onChange={(e) => updateSearchValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitSearchMobile() }}
                      aria-label="Search"
                      className="min-w-0 flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-brand"
                    />
                    <button
                      type="button"
                      onClick={submitSearchMobile}
                      className="inline-flex items-center justify-center h-11 w-11 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10"
                      aria-label="Search"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85" aria-hidden="true">
                        <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.98 4 11.49 4 9 4 7 6.01 7 8.5S9 13 11.49 13c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l4 3.99L20.49 18l-3.99-4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-white/50">Pixe</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => switchApp('publish')} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left hover:bg-white/10">
                    <div className="text-sm font-semibold">Publish</div>
                    <div className="text-xs text-white/60">Home feed</div>
                  </button>
                  <button onClick={() => switchApp('saved')} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left hover:bg-white/10">
                    <div className="text-sm font-semibold">Saved</div>
                    <div className="text-xs text-white/60">Bookmarks</div>
                  </button>
                  <button onClick={() => switchApp('people')} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left hover:bg-white/10">
                    <div className="text-sm font-semibold">People</div>
                    <div className="text-xs text-white/60">Creators</div>
                  </button>
                  <button onClick={() => switchApp('pages')} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left hover:bg-white/10">
                    <div className="text-sm font-semibold">Pages</div>
                    <div className="text-xs text-white/60">Businesses</div>
                  </button>
                  <button onClick={() => switchApp('store')} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left hover:bg-white/10">
                    <div className="text-sm font-semibold">Store</div>
                    <div className="text-xs text-white/60">Listings</div>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-white/50">Quick</div>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/inbox" onClick={() => setMobileMenuOpen(false)} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                    <div className="text-sm font-semibold">Inbox</div>
                    {authed && unread > 0 ? <div className="text-xs text-white/60">{unread > 99 ? '99+' : unread} unread</div> : <div className="text-xs text-white/60">Messages</div>}
                  </Link>
                  <Link to="/store" onClick={() => setMobileMenuOpen(false)} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                    <div className="text-sm font-semibold">Cart</div>
                    <div className="text-xs text-white/60">Store</div>
                  </Link>
                </div>
              </div>

              {!authed ? (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-white/50">Account</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="rounded-xl bg-emerald-500 px-3 py-3 text-center font-semibold text-white hover:bg-emerald-600">
                      Sign up
                    </Link>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-center font-semibold hover:bg-white/10">
                      Log in
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-white/50">Account</div>
                  <div className="space-y-2">
                    {profileHref && (
                      <Link to={profileHref} onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                        <div className="text-sm font-semibold">Profile</div>
                        <div className="text-xs text-white/60">{me?.profile?.handle ? `@${me.profile.handle.replace(/^@/, '')}` : me?.username}</div>
                      </Link>
                    )}
                    <Link to="/settings" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                      <div className="text-sm font-semibold">Settings</div>
                      <div className="text-xs text-white/60">Account and preferences</div>
                    </Link>
                    <Link to="/settings/wallet" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                      <div className="text-sm font-semibold">Wallet</div>
                      <div className="text-xs text-white/60">PIXE and payouts</div>
                    </Link>
                    <Link to="/pros/requests" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                      <div className="text-sm font-semibold">Pros Requests</div>
                      <div className="text-xs text-white/60">
                        {prosRequestsPending > 0 ? `${prosRequestsPending} pending` : 'No pending requests'}
                      </div>
                    </Link>
                    <Link to="/jobs" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                      <div className="text-sm font-semibold">Jobs</div>
                      <div className="text-xs text-white/60">Created and assigned</div>
                    </Link>
                    <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10">
                      <div className="text-sm font-semibold">Pricing</div>
                      <div className="text-xs text-white/60">Creator plans</div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => { clearToken(); window.location.reload() }}
                      className="w-full text-left rounded-xl border border-white/10 bg-white/5 px-3 py-3 hover:bg-white/10"
                    >
                      <div className="text-sm font-semibold">Sign out</div>
                      <div className="text-xs text-white/60">End session</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
